// People and their effective-dated records (PRD §2.2, EC1–EC5, EC11). A change adds a record with
// its own start date; nothing is edited in place. A record entered in error is voided with a
// reason, and stays in the document so the history reads true.

import { isIsoDate, type Iso } from "./dates";
import type { CostItem, EmployeeType } from "./cost";

export type PayType = EmployeeType;

export const PAY_TYPES: readonly PayType[] = ["exempt", "hourly", "contractor-hourly", "contractor-monthly", "partner"];

export const PAY_TYPE_LABEL: Readonly<Record<PayType, string>> = {
  exempt: "FTE – exempt",
  hourly: "FTE – hourly",
  "contractor-hourly": "1099 – hourly",
  "contractor-monthly": "1099 – monthly fee",
  partner: "Partner (K-1)",
};

/** What the comp record's `amount` means for each pay type. */
export const PAY_AMOUNT_LABEL: Readonly<Record<PayType, string>> = {
  exempt: "Annual base",
  hourly: "Hourly rate",
  "contractor-hourly": "Hourly rate",
  "contractor-monthly": "Monthly fee",
  partner: "Annual guaranteed payments",
};

/** On payroll (a W-2): employer taxes, time off and paydays. Partners and 1099s are not. */
export const isEmployee = (type: PayType): boolean => type === "exempt" || type === "hourly";

/** Paid a flat amount whatever the hours: a salary, a partner's guaranteed payments, or a monthly fee. */
export const isFlatPay = (type: PayType | null): boolean => type === "exempt" || type === "partner" || type === "contractor-monthly";

/** The unit a comp record's amount is shown in. */
export const PAY_UNIT: Readonly<Record<PayType, string>> = { exempt: "/yr", hourly: "/h", "contractor-hourly": "/h", "contractor-monthly": "/mo", partner: "/yr" };

/** Annual pay, for showing OTE (PRD §3.4): a salary or guaranteed payments as is, hourly rates at 2,080 h, monthly fees × 12. */
export const annualPayOf = (comp: { type: PayType; amount: number }): number =>
  comp.type === "exempt" || comp.type === "partner" ? comp.amount : comp.type === "contractor-monthly" ? comp.amount * 12 : comp.amount * 2_080;

export const COST_CATEGORIES = [
  "healthcare",
  "dental-vision",
  "retirement",
  "equipment",
  "software",
  "phone-internet",
  "reimbursements",
  "training",
  "other",
] as const;
export type CostCategory = (typeof COST_CATEGORIES)[number];

export const COST_CATEGORY_LABEL: Readonly<Record<CostCategory, string>> = {
  healthcare: "Healthcare",
  "dental-vision": "Dental / vision",
  retirement: "Retirement match",
  equipment: "Equipment",
  software: "Software",
  "phone-internet": "Phone / internet",
  reimbursements: "Reimbursements",
  training: "Training",
  other: "Other",
};

/** Default capacity (PRD §2.1) and planned PTO when a person has no capacity record. */
export const DEFAULT_CAPACITY = 45;
export const DEFAULT_PTO_DAYS = 15;

export interface Entered {
  id: string;
  enteredAt: string;
  enteredBy: string;
  voided?: { at: string; by: string; reason: string };
}

export interface CompRecord extends Entered {
  from: Iso;
  type: PayType;
  /** Annual base (exempt), hourly rate (hourly and 1099 hourly) or monthly fee (1099 monthly). */
  amount: number;
  note?: string;
}

export interface CostItemRecord extends Entered, CostItem {
  category: CostCategory;
}

export interface CapacityRecord extends Entered {
  from: Iso;
  hoursPerWeek: number;
  /** Planned PTO days per year. */
  ptoDays: number;
}

/**
 * Work away from home for a stretch (TX1): from and to, both included, in a state. Hours on those days
 * count toward that state instead of the home state.
 */
export interface TravelRecord extends Entered {
  from: Iso;
  to: Iso;
  state: string;
  note?: string;
}

export interface LocationRecord extends Entered {
  from: Iso;
  /** Two-letter home work state (TX1). */
  state: string;
}

/** A compensation letter's bonus: an annual target per bonus type, from a date (PRD §3.4, rev. 1.6). */
export interface BonusTargetRecord extends Entered {
  from: Iso;
  targets: { typeId: string; amount: number }[];
  /** e.g. "2026 compensation letter". */
  note?: string;
}

/** Replaces one type's amount (typeId) or the whole bonus (typeId null) from `from`, optionally until `to`. */
export interface BonusOverrideRecord extends Entered {
  from: Iso;
  to?: Iso;
  typeId: string | null;
  amount: number;
  note?: string;
}

export interface PersonProfile {
  name: string;
  title?: string;
  roleId?: string;
  timeZone: string;
  /** The person's 3Seven account, when they have one. */
  email?: string;
  /** Resource names in today's workbooks ("Carlson_2026-1"), used only to map imported rows. */
  payIds: string[];
  status: "active" | "archived";
  /** Last day of work. After it, the person leaves capacity (EC11). */
  endDate?: Iso;
}

export interface Person extends PersonProfile {
  id: string;
  compRecords: CompRecord[];
  costItems: CostItemRecord[];
  capacity: CapacityRecord[];
  locations: LocationRecord[];
  /** Absent on people added before rev. 1.6; read as empty. */
  bonusTargets?: BonusTargetRecord[];
  bonusOverrides?: BonusOverrideRecord[];
  /** Travel overrides of the work state (TX1); absent on older documents. */
  travel?: TravelRecord[];
}

export const RECORD_KINDS = ["comp", "costItem", "capacity", "location", "bonusTarget", "bonusOverride", "travel"] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];

/** Where each kind of record lives on a Person. */
export const RECORD_FIELD = {
  comp: "compRecords",
  costItem: "costItems",
  capacity: "capacity",
  location: "locations",
  bonusTarget: "bonusTargets",
  bonusOverride: "bonusOverrides",
  travel: "travel",
} as const satisfies Record<RecordKind, keyof Person>;

/** A person's records of one kind; older documents may lack the bonus lists. */
export const recordsOf = (p: Person, kind: RecordKind): (Entered & { from: Iso })[] => (p[RECORD_FIELD[kind]] as (Entered & { from: Iso })[] | undefined) ?? [];

/** Pay, associated costs and bonus are pay data (comp.read); capacity and work state are not. */
export const isPayRecord = (kind: RecordKind): boolean => kind !== "capacity" && kind !== "location" && kind !== "travel";

export const live = <T extends Entered>(records: readonly T[]): T[] => records.filter((r) => !r.voided);

/**
 * The record in effect on `day`: the latest live one starting on or before it. When two start on
 * the same day, the one entered later wins.
 */
export function inEffect<T extends Entered & { from: Iso }>(records: readonly T[], day: Iso): T | undefined {
  let best: T | undefined;
  for (const r of records) {
    if (r.voided || r.from > day) continue;
    if (!best || r.from > best.from || (r.from === best.from && r.enteredAt > best.enteredAt)) best = r;
  }
  return best;
}

/** Live cost items, as the cost functions take them. */
export const costItemsOf = (p: Person): CostItem[] => live(p.costItems);

/** Working on `day`: no end date, or an end date on or after it. Archiving always sets an end date. */
export const isActiveOn = (p: Pick<Person, "endDate">, day: Iso): boolean => !p.endDate || p.endDate >= day;

export const STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
  "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
] as const;

export const TIME_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
] as const;

export const DEFAULT_TIME_ZONE = "America/Chicago";

// --- Validation: shared by the API (authoritative) and the browser (as-you-type) -----------------

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const text = (v: unknown, max = 200): v is string => typeof v === "string" && v.trim().length > 0 && v.length <= max;

export function validateProfile(p: Partial<PersonProfile>): string[] {
  const errors: string[] = [];
  if (!text(p.name, 120)) errors.push("A name is required.");
  if (p.title !== undefined && p.title !== "" && !text(p.title, 120)) errors.push("The title is too long.");
  if (p.timeZone !== undefined && !(TIME_ZONES as readonly string[]).includes(p.timeZone)) errors.push("Pick a time zone from the list.");
  if (p.email !== undefined && p.email !== "" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(p.email)) errors.push("The account email doesn't look like an email address.");
  if (p.payIds !== undefined && (!Array.isArray(p.payIds) || p.payIds.some((id) => !text(id, 60)))) errors.push("Pay IDs must be short names.");
  if (p.status !== undefined && p.status !== "active" && p.status !== "archived") errors.push("Status must be active or archived.");
  if (p.endDate !== undefined && p.endDate !== "" && !isIsoDate(p.endDate)) errors.push("The end date isn't a valid date.");
  if (p.status === "archived" && !p.endDate) errors.push("Archiving needs an end date: the last day of work.");
  return errors;
}

const dollars = (v: unknown) => typeof v === "number" && Number.isFinite(v) && v >= 0 && v < 10_000_000;

export function validateBonusTargets(r: Partial<BonusTargetRecord>, typeIds?: ReadonlySet<string>): string[] {
  const errors: string[] = [];
  if (!isIsoDate(r.from)) errors.push("An effective date is required: when the letter's targets start.");
  if (!Array.isArray(r.targets)) return [...errors, "List the targets by bonus type."];
  const seen = new Set<string>();
  for (const t of r.targets) {
    if (typeof t?.typeId !== "string" || (typeIds && !typeIds.has(t.typeId))) errors.push("Every target needs a bonus type from the list.");
    else if (seen.has(t.typeId)) errors.push("A bonus type is listed twice.");
    else seen.add(t.typeId);
    if (!dollars(t?.amount)) errors.push("Every target is an annual dollar amount of zero or more.");
  }
  if (r.note !== undefined && (typeof r.note !== "string" || r.note.length > 300)) errors.push("The note is too long.");
  return [...new Set(errors)];
}

export function validateBonusOverride(r: Partial<BonusOverrideRecord>, typeIds?: ReadonlySet<string>): string[] {
  const errors: string[] = [];
  if (!isIsoDate(r.from)) errors.push("An effective date is required.");
  if (r.to !== undefined && (!isIsoDate(r.to) || (isIsoDate(r.from) && r.to < r.from))) errors.push("The end date must be on or after the start.");
  if (r.typeId !== null && (typeof r.typeId !== "string" || (typeIds && !typeIds.has(r.typeId)))) errors.push("Pick a bonus type, or the whole bonus.");
  if (!dollars(r.amount)) errors.push("The override is an annual dollar amount of zero or more.");
  if (r.note !== undefined && (typeof r.note !== "string" || r.note.length > 300)) errors.push("The note is too long.");
  return errors;
}

/**
 * Checks the fields a user supplies for a new record. The id and entry stamps are added by the API.
 * Bonus records check their types against `bonusTypeIds` when it is given.
 */
export function validateRecord(kind: RecordKind, r: Record<string, unknown>, bonusTypeIds?: ReadonlySet<string>): string[] {
  if (kind === "bonusTarget") return validateBonusTargets(r as Partial<BonusTargetRecord>, bonusTypeIds);
  if (kind === "bonusOverride") return validateBonusOverride(r as Partial<BonusOverrideRecord>, bonusTypeIds);
  const errors: string[] = [];
  if (!isIsoDate(r.from)) errors.push("An effective date is required.");
  switch (kind) {
    case "comp":
      if (!(PAY_TYPES as readonly string[]).includes(r.type as string)) errors.push("Pick a pay type.");
      if (!isNum(r.amount) || r.amount <= 0) errors.push("The pay amount must be a positive number.");
      else if (r.type === "exempt" && r.amount < 1_000) errors.push("Exempt pay is an annual amount; this looks like an hourly rate.");
      else if ((r.type === "hourly" || r.type === "contractor-hourly") && r.amount > 2_000) errors.push("This is an hourly rate; the amount looks annual.");
      if (r.note !== undefined && r.note !== "" && !text(r.note, 300)) errors.push("The note is too long.");
      break;
    case "costItem":
      if (!(COST_CATEGORIES as readonly string[]).includes(r.category as string)) errors.push("Pick a cost category.");
      if (!text(r.label, 80)) errors.push("Give the cost a short label.");
      if (!isNum(r.amount) || r.amount < 0) errors.push("The cost amount must be zero or more.");
      if (!["monthly", "annual", "one-time"].includes(r.frequency as string)) errors.push("Pick monthly, annual or one-time.");
      if (r.to !== undefined && (!isIsoDate(r.to) || (isIsoDate(r.from) && r.to < r.from))) errors.push("The end date must be on or after the start date.");
      if (r.frequency === "one-time" && r.amortizeMonths !== undefined && (!Number.isInteger(r.amortizeMonths) || (r.amortizeMonths as number) < 1 || (r.amortizeMonths as number) > 120)) {
        errors.push("Spread a one-time cost over 1 to 120 months.");
      }
      break;
    case "capacity":
      if (!isNum(r.hoursPerWeek) || r.hoursPerWeek <= 0 || r.hoursPerWeek > 80) errors.push("Capacity must be between 0 and 80 hours a week.");
      if (!isNum(r.ptoDays) || r.ptoDays < 0 || r.ptoDays > 60) errors.push("Planned PTO must be between 0 and 60 days a year.");
      break;
    case "location":
      if (!(STATES as readonly string[]).includes(r.state as string)) errors.push("Pick a state.");
      break;
    case "travel":
      if (!(STATES as readonly string[]).includes(r.state as string)) errors.push("Pick a state.");
      if (!isIsoDate(r.to) || (isIsoDate(r.from) && (r.to as string) < (r.from as string))) errors.push("The last day must be on or after the first.");
      if (r.note !== undefined && r.note !== "" && !text(r.note, 200)) errors.push("The note is too long.");
      break;
  }
  return errors;
}

/** The user-supplied fields of each record kind; anything else in a request is dropped. */
export function pickRecordFields(kind: RecordKind, r: Record<string, unknown>): Record<string, unknown> {
  const keep: Record<RecordKind, string[]> = {
    comp: ["from", "type", "amount", "note"],
    costItem: ["from", "to", "category", "label", "amount", "frequency", "amortizeMonths"],
    capacity: ["from", "hoursPerWeek", "ptoDays"],
    location: ["from", "state"],
    bonusTarget: ["from", "targets", "note"],
    bonusOverride: ["from", "to", "typeId", "amount", "note"],
    travel: ["from", "to", "state", "note"],
  };
  const out = Object.fromEntries(keep[kind].filter((k) => r[k] !== undefined && r[k] !== "").map((k) => [k, r[k]]));
  if (kind === "bonusTarget" && Array.isArray(out.targets)) {
    out.targets = (out.targets as { typeId?: unknown; amount?: unknown }[]).map((t) => ({ typeId: t?.typeId, amount: t?.amount }));
  }
  if (kind === "bonusOverride" && !("typeId" in out)) out.typeId = null;
  return out;
}

/** The state a person works in on a day (TX1): a travel override covering it, else the home state in effect. */
export function workStateOn(person: Pick<Person, "locations" | "travel">, day: Iso): string | null {
  let trip: TravelRecord | undefined;
  for (const t of live(person.travel ?? [])) if (t.from <= day && t.to >= day && (!trip || t.enteredAt > trip.enteredAt)) trip = t;
  return trip?.state ?? inEffect(person.locations, day)?.state ?? null;
}
